import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

const STATUSES = ['draft', 'trialing', 'active', 'past_due', 'canceled', 'expired'];
const CYCLES = ['monthly', 'quarterly', 'annual', 'one_time', 'custom'];

const emptyForm = {
  tenant_id: '',
  plan_id: '',
  subscription_status: 'draft',
  billing_cycle: 'monthly',
  start_date: '',
  end_date: '',
  renewal_date: '',
  cancellation_date: '',
  price_snapshot: '',
  notes: ''
};

export default function PlatformTenantSubscriptionFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user, ready } = usePlatformAuth();

  const [form, setForm] = useState(emptyForm);
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ready && user?.role !== 'super_admin') {
      navigate('/platform/tenant-subscriptions', { replace: true });
    }
  }, [ready, user, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tr, pr] = await Promise.all([
          platformApiFetch('/api/platform/tenants?pageSize=500&sort=tenant_name&order=asc'),
          platformApiFetch('/api/platform/plans?pageSize=200&sort=name&order=asc')
        ]);
        const tj = await tr.json().catch(() => ({}));
        const pj = await pr.json().catch(() => ({}));
        if (!cancelled) {
          if (tr.ok) setTenants(tj.tenants || []);
          if (pr.ok) setPlans(pj.plans || []);
        }
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/tenant-subscriptions/${id}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      const s = json.subscription;
      setForm({
        tenant_id: String(s.tenant_id),
        plan_id: String(s.plan_id),
        subscription_status: s.subscription_status || 'draft',
        billing_cycle: s.billing_cycle || 'monthly',
        start_date: s.start_date || '',
        end_date: s.end_date || '',
        renewal_date: s.renewal_date || '',
        cancellation_date: s.cancellation_date || '',
        price_snapshot: s.price_snapshot != null ? String(s.price_snapshot) : '',
        notes: s.notes || ''
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id, isEdit]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const body = {
      plan_id: Number(form.plan_id),
      subscription_status: form.subscription_status,
      billing_cycle: form.billing_cycle,
      start_date: form.start_date,
      notes: form.notes.trim() || null
    };
    if (form.end_date) body.end_date = form.end_date;
    else if (isEdit) body.end_date = null;
    if (form.renewal_date) body.renewal_date = form.renewal_date;
    else if (isEdit) body.renewal_date = null;
    if (form.cancellation_date) body.cancellation_date = form.cancellation_date;
    else if (isEdit) body.cancellation_date = null;
    if (form.price_snapshot !== '') {
      body.price_snapshot = Number(form.price_snapshot);
    } else if (!isEdit) {
      /* omit — server defaults from plan */
    }

    if (!isEdit) {
      body.tenant_id = Number(form.tenant_id);
    }

    try {
      const res = await platformApiFetch(
        isEdit ? `/api/platform/tenant-subscriptions/${id}` : '/api/platform/tenant-subscriptions',
        {
          method: isEdit ? 'PATCH' : 'POST',
          body
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Save failed');
      navigate(`/platform/tenant-subscriptions/${json.subscription.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        {isEdit ? 'Edit subscription' : 'New subscription'}
      </Typography>
      <Button sx={{ mb: 2 }} onClick={() => navigate(isEdit ? `/platform/tenant-subscriptions/${id}` : '/platform/tenant-subscriptions')}>
        Back
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper component="form" onSubmit={submit} sx={{ p: 3, maxWidth: 720 }}>
        {!isEdit && (
          <>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Tenant</InputLabel>
              <Select label="Tenant" value={form.tenant_id} onChange={(e) => set('tenant_id', e.target.value)}>
                {tenants.map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>
                    {t.tenant_name} ({t.tenant_code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Alert severity="info" sx={{ mb: 1 }}>
              You cannot move a subscription to another tenant after creation.
            </Alert>
          </>
        )}

        <FormControl fullWidth margin="normal" required>
          <InputLabel>Plan</InputLabel>
          <Select label="Plan" value={form.plan_id} onChange={(e) => set('plan_id', e.target.value)}>
            {plans
              .filter((p) => p.status === 'active')
              .map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>
                  {p.name} ({p.code})
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={form.subscription_status} onChange={(e) => set('subscription_status', e.target.value)}>
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel>Billing cycle</InputLabel>
          <Select label="Billing cycle" value={form.billing_cycle} onChange={(e) => set('billing_cycle', e.target.value)}>
            {CYCLES.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Start date"
          type="date"
          fullWidth
          required
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={form.start_date}
          onChange={(e) => set('start_date', e.target.value)}
        />
        <TextField
          label="End date"
          type="date"
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={form.end_date}
          onChange={(e) => set('end_date', e.target.value)}
        />
        <TextField
          label="Renewal date"
          type="date"
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={form.renewal_date}
          onChange={(e) => set('renewal_date', e.target.value)}
        />
        <TextField
          label="Cancellation date"
          type="date"
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={form.cancellation_date}
          onChange={(e) => set('cancellation_date', e.target.value)}
        />
        <TextField
          label="Price snapshot (leave blank to default from plan)"
          fullWidth
          margin="normal"
          value={form.price_snapshot}
          onChange={(e) => set('price_snapshot', e.target.value)}
        />
        <TextField
          label="Notes"
          fullWidth
          margin="normal"
          multiline
          minRows={3}
          value={form.notes}
          onChange={(e) => set('notes', e.target.value)}
        />

        <Box sx={{ mt: 2 }}>
          <Button type="submit" variant="contained" disabled={saving || (!isEdit && !form.tenant_id)}>
            {saving ? <CircularProgress size={22} /> : 'Save'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
